import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Brand from "@/components/public/Brand";
import { ThemeToggle } from "@/components/public/PublicLayout";

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState("email"); // email | reset
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const requestReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/auth/forgot-password", { email });
      setStep("reset");
      toast.success("If an account exists, a reset code was sent to your email");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  const reset = async (e) => {
    e.preventDefault();
    if (newPassword !== confirm) { toast.error("Passwords do not match"); return; }
    if (newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { email, code: otp, new_password: newPassword });
      toast.success("Password updated. Please sign in with your new password.");
      navigate("/login");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-secondary/40 p-6">
      <div className="mb-6 flex w-full max-w-sm items-center justify-between">
        <Link to="/" className="flex items-center gap-2">
          <Brand height={38} textClass="font-display text-lg font-extrabold" />
        </Link>
        <ThemeToggle />
      </div>
      <div className="w-full max-w-sm rounded-2xl border border-border bg-card p-8">
        <h1 className="font-display text-2xl font-bold">Reset Password</h1>
        <p className="mt-1 text-sm text-muted-foreground">{step === "email" ? "Enter your email to receive a reset code." : "Enter the code and choose a new password."}</p>

        {step === "email" ? (
          <form onSubmit={requestReset} className="mt-6 grid gap-4">
            <div><Label>Email</Label><Input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" /></div>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="forgot-submit">{busy ? "Sending..." : "Send Reset Code"}</Button>
          </form>
        ) : (
          <form onSubmit={reset} className="mt-6 grid gap-4">
            <div><Label>Verification Code</Label><Input required value={otp} onChange={(e) => setOtp(e.target.value)} maxLength={6} placeholder="000000" data-testid="reset-otp" /></div>
            <div><Label>New Password</Label><Input required type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} data-testid="reset-password" /></div>
            <div><Label>Confirm New Password</Label><Input required type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="reset-confirm" /></div>
            <Button type="submit" disabled={busy} className="rounded-full" data-testid="reset-submit">{busy ? "Updating..." : "Reset Password"}</Button>
            <button type="button" onClick={() => setStep("email")} className="text-xs text-muted-foreground hover:text-primary">← Change email</button>
          </form>
        )}

        <p className="mt-6 text-center text-sm text-muted-foreground">Remembered it? <Link to="/login" className="font-semibold text-primary hover:underline">Sign in</Link></p>
      </div>
    </div>
  );
}
